import React from "react";
import { toast } from "sonner";
import type { AxiosResponse } from "axios";
import type { PublishShortPostRequest, PublishShortPostResponse } from "@/generated-client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import { getSafeToastMessageFromError } from "@/main/common/ApiUtils";
import { MetaHandler } from "@/main/common/MetaHandler";
import {
    buildSharedComposerSeedFromDocument,
    buildSharedComposerSeedSourceJson,
    writeSharedComposerSeed,
} from "@/main/composer/sharedComposerSeed";
import { CREATE_DISCUSSION_PATH } from "@/main/navigation/composerRoutes";
import { createClientUuid } from "@/main/util/createClientUuid";
import type { Localizer } from "@/main/react/Types";
import ShortPostEditorLazy, {
    preloadShortPostEditor,
    type ShortPostEditorChange,
} from "@/main/react/shortpost/ShortPostEditorLazy";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * SV-136 D5: a short post is at most 5,000 VISIBLE characters (was 360, round 31 A1). Mirrors
 * `RequestValidationConstants.SHORT_POST_BODY_MAX_VISIBLE_CHARS`; the server is the authority and
 * refuses anything over it with the composer's existing TipTap key, so this bound only decides when
 * the button stops offering a publish that would be refused.
 */
export const SHORT_POST_MAX_VISIBLE_CHARS = 5000;

/**
 * SV-136 D6: at most 4 media in a short post (was 1). Mirrors
 * `RequestValidationConstants.SHORT_POST_MAX_IMAGES`; enforced in the editor by MediaLimit, which also
 * counts a video embed, so the client is the stricter of the two.
 */
export const SHORT_POST_MAX_MEDIA = 4;

/**
 * Counted as the server counts `shortPostVisibleText` (Jsoup `text()`, then `codePointCount`):
 * whitespace runs collapse to one space, the ends are trimmed, and an emoji is one.
 */
export function countShortPostCharacters(text: string): number {
    return Array.from(text.replace(/\s+/g, " ").trim()).length;
}

const BACKUP_KEY_PREFIX = "seavien.shortPost.unsent.v1:";

function backupKey(draftKey: string): string {
    return `${BACKUP_KEY_PREFIX}${MetaHandler.getLoggedInUsername() ?? "anonymous"}:${draftKey}`;
}

// A9: every storage access is wrapped. A browser that refuses storage (private mode, quota, blocked
// site data) costs the backup and nothing else.
function readBackup(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeBackup(key: string, sourceJson: string | null): void {
    try {
        if (sourceJson) {
            window.localStorage.setItem(key, sourceJson);
        } else {
            window.localStorage.removeItem(key);
        }
    } catch {
        // Silent by decision (A9): no notice, no copy.
    }
}

const EMPTY_CHANGE: ShortPostEditorChange = { sourceJson: "", visibleCharacters: 0, hasContent: false };

/**
 * The one field and the one button both short-post boxes share: the box atop the home feed (publish)
 * and the reply box under a short post (reply). SV-136 A1: swapping the field here upgrades both.
 *
 * <p><b>Collapsed until focus (A7).</b> At rest it is one plain line with no TipTap on the page; the
 * rich editor is a lazy chunk (A8) that pointerdown/focus warm with `preload()`, which fetches without
 * changing the rendered tree (rule 26). Focus is the member asking to write, so THAT swaps the line for
 * the editor. While the chunk arrives the same line stays (the Suspense fallback), autofocused and
 * keeping whatever was typed into it, and the editor opens on those words.
 *
 * <p><b>Idempotent across retries (A2).</b> One `clientRequestId` per piece of text, minted when the
 * box mounts and again only after a publish SUCCEEDS. A retry after a failure re-sends the same id.
 *
 * <p><b>The body is the editor's own document</b> (SV-136): `sourceJson` is `editor.getJSON()`, so
 * formatting and embeds survive into the published post. Local state only; the editor reports each
 * change through a handler, never through an effect, so typing is one commit per character
 * (AGENTS/frontend-store-subscriptions.md, the composer #185 block).
 *
 * <p><b>Unsent words are backed up silently (A9)</b>, per signed-in member and per box, restored on
 * return (the box opens straight into the editor), and cleared when a publish succeeds.
 *
 * <p><b>What happens after a publish is an explicit mode ({@link ShortPostAfterPublish}).</b> The
 * composer atop the feed (`navigate`, the default) lands the member on the new post, exactly as the
 * long-form publish does (SV-141 A6). A feed card's reply box (`stay`) keeps the member in the feed and
 * hands the published post and its submitted body to the caller, which renders it under the card
 * (SV-141 D3). Either way the box clears first.
 */
export type ShortPostAfterPublish =
    | { mode: "navigate"; navigationSource: string }
    | {
        mode: "stay";
        onPublished: (published: { response: PublishShortPostResponse; sourceJson: string }) => void;
    };

export function ShortPostForm(props: {
    localizer: Localizer;
    placeholder: string;
    submitLabel: string;
    submit: (request: PublishShortPostRequest) => Promise<AxiosResponse<PublishShortPostResponse>>;
    /** Kept for the composer's call site; ignored when `afterPublish` is given. */
    navigationSource?: string;
    /** Defaults to `navigate` with `navigationSource`, the composer's behaviour. */
    afterPublish?: ShortPostAfterPublish;
    playwrightId: string;
    /** Scopes the unsent-words backup; defaults to `playwrightId`. A reply box passes its target. */
    draftKey?: string;
    /**
     * FI-596: over the limit, offer "Continue as a full post" under the counter. The composer atop the
     * feed only: a reply box never offers it, since a reply turned into a standalone post would drop what
     * it replies to, which nobody settled.
     */
    offerFullPostOverLimit?: boolean;
    className?: string;
}) {
    const { localizer, placeholder, submitLabel, submit, playwrightId, className } = props;
    const afterPublish: ShortPostAfterPublish = props.afterPublish
        ?? { mode: "navigate", navigationSource: props.navigationSource ?? playwrightId };
    const storageKey = backupKey(props.draftKey ?? playwrightId);

    // SV-145 D6: on a phone, the open editor carries Post and the counter inside its card.
    const isPhone = useIsMobile();
    const [restoredSourceJson] = React.useState<string | null>(() => readBackup(storageKey));
    const [expanded, setExpanded] = React.useState<boolean>(() => restoredSourceJson !== null);
    const [seedText, setSeedText] = React.useState("");
    const [change, setChange] = React.useState<ShortPostEditorChange>(EMPTY_CHANGE);
    // A successful publish remounts the editor empty, rather than reaching into it.
    const [editorGeneration, setEditorGeneration] = React.useState(0);
    const [submitting, setSubmitting] = React.useState(false);
    const clientRequestIdRef = React.useRef<string>(createClientUuid());
    const inFlightRef = React.useRef(false);

    const length = change.visibleCharacters;
    const overLimit = length > SHORT_POST_MAX_VISIBLE_CHARS;
    const canSubmit = change.hasContent && !overLimit && !submitting;

    const handleEditorChange = React.useCallback((next: ShortPostEditorChange) => {
        setChange(next);
        writeBackup(storageKey, next.hasContent ? next.sourceJson : null);
    }, [storageKey]);

    const expand = React.useCallback(() => {
        preloadShortPostEditor();
        setExpanded(true);
    }, []);

    const submitNow = async () => {
        if (!canSubmit || inFlightRef.current) {
            return;
        }
        inFlightRef.current = true;
        setSubmitting(true);
        try {
            const sourceJson = change.sourceJson;
            const response = await submit({
                sourceJson,
                clientRequestId: clientRequestIdRef.current,
            });
            clientRequestIdRef.current = createClientUuid();
            writeBackup(storageKey, null);
            setChange(EMPTY_CHANGE);
            setSeedText("");
            setEditorGeneration((generation) => generation + 1);
            if (afterPublish.mode === "stay") {
                if (response.data) {
                    afterPublish.onPublished({ response: response.data, sourceJson });
                }
            } else {
                const redirectTo = response.data?.redirectTo;
                if (redirectTo) {
                    navigateToUrl(redirectTo, { source: afterPublish.navigationSource });
                }
            }
        } catch (error) {
            toast.error(getSafeToastMessageFromError(error, localizer("shared.action.failed")));
        } finally {
            inFlightRef.current = false;
            setSubmitting(false);
        }
    };

    // FI-596: the member's own document goes to the long-form composer through the shared composer seed
    // (the chat door's hand-off). The unsent-words backup is deliberately left in place: the composer
    // applies a seed only to a fresh, empty draft, so if it declines, the words are still here on return.
    const continueAsFullPost = () => {
        const seed = buildSharedComposerSeedFromDocument(change.sourceJson, Date.now());
        if (!seed || !writeSharedComposerSeed(seed)) {
            toast.error(localizer("shared.action.failed"));
            return;
        }
        navigateToUrl(CREATE_DISCUSSION_PATH, { source: `${playwrightId}-continue-as-full-post` });
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void submitNow();
    };

    // QB-2: the editor's Mod+Enter runs the SAME submit as the Post button (same gate, same in-flight
    // guard, same request id). Through a ref, because `submitNow` closes over this render's `change`
    // and the editor holds its callback for its whole life.
    const submitNowRef = React.useRef(submitNow);
    submitNowRef.current = submitNow;
    const handleSubmitShortcut = React.useCallback(() => {
        void submitNowRef.current();
    }, []);

    // The one-line box: the resting state, and the placeholder while the editor chunk arrives.
    const renderLine = (whileLoading: boolean) => (
        <Textarea
            value={seedText}
            onChange={(event) => setSeedText(event.target.value)}
            onPointerDown={preloadShortPostEditor}
            onFocus={whileLoading ? undefined : expand}
            autoFocus={whileLoading}
            placeholder={placeholder}
            aria-label={placeholder}
            rows={1}
            className="min-h-10 flex-1 resize-none"
        />
    );

    // The first mount after a restore opens on the backup; a mount after a publish opens empty.
    const initialSourceJson = editorGeneration === 0 && restoredSourceJson
        ? restoredSourceJson
        : seedText.trim()
            ? buildSharedComposerSeedSourceJson(seedText.trim())
            : null;

    const renderActions = (inCard: boolean) => (
            <div className={inCard ? "contents" : "flex shrink-0 flex-col items-end gap-1"}>
                {length > 0 ? (
                    <span
                        className={cn("text-xs tabular-nums", overLimit ? "text-destructive-text" : "text-muted-foreground")}
                        data-playwright-id={`${playwrightId}-counter`}
                    >
                        {localizer("chat.composer.counter", length, SHORT_POST_MAX_VISIBLE_CHARS)}
                    </span>
                ) : null}
                {overLimit && props.offerFullPostOverLimit ? (
                    <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto px-0 py-0 text-xs"
                        data-playwright-id={`${playwrightId}-continue-as-full-post`}
                        onClick={continueAsFullPost}
                    >
                        {localizer("short_post.composer.continue_as_full_post")}
                    </Button>
                ) : null}
                <Button type="submit" size="sm" disabled={!canSubmit}>
                    {submitLabel}
                </Button>
            </div>
    );

    // On a phone the editor draws the actions in its own footer; the line shown while its chunk
    // arrives is still the one-line box, so it keeps Post beside it (D6).
    const actionsInCard = expanded && isPhone;

    return (
        <form
            className={cn("flex min-w-0 items-end gap-2", className)}
            data-playwright-id={playwrightId}
            onSubmit={handleSubmit}
        >
            {expanded ? (
                <div className="min-w-0 flex-1">
                    <ShortPostEditorLazy
                        key={editorGeneration}
                        localizer={localizer}
                        placeholder={placeholder}
                        initialSourceJson={initialSourceJson}
                        onChange={handleEditorChange}
                        onSubmitShortcut={handleSubmitShortcut}
                        disabled={submitting}
                        autoFocus={restoredSourceJson === null || editorGeneration > 0}
                        footer={actionsInCard ? renderActions(true) : undefined}
                        fallback={actionsInCard
                            ? <div className="flex min-w-0 items-end gap-2">{renderLine(true)}{renderActions(false)}</div>
                            : renderLine(true)}
                    />
                </div>
            ) : (
                renderLine(false)
            )}
            {actionsInCard ? null : renderActions(false)}
        </form>
    );
}
